import * as React from 'react';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { PageHeader } from '../../../../components/layout/PageHeader';
import { Button } from '../../../../components/ui/Button';
import { Money } from '../../../../components/ui/Money';
import { StatusBadge } from '../../../../components/ui/StatusBadge';
import { useToast } from '../../../../components/ui/Toast';
import { useBankInstruments, useUpdateBankInstrumentStatus, type BankInstrument } from '../../../../features/finance/hooks';
import { Banknote, CheckCircle, Clock, RotateCcw } from 'lucide-react';
import { usePageTitle } from '../../../../lib/hooks';
import { defineListPage, useListPage, type PaginatedQueryFn } from '@repo/list';
import { DataTable, createUrlAdapter } from '@repo/web-list';
import { fetcher } from '../../../../lib/api-fetcher';

export const Route = createFileRoute('/_auth/finance/cheque-register/')({
  component: ChequeRegisterPage,
});

const STATUS_CONFIG: Record<string, { label: string; variant: 'info' | 'warning' | 'success' | 'danger' }> = {
  RECEIVED: { label: 'Received', variant: 'info' },
  DEPOSITED: { label: 'Deposited', variant: 'warning' },
  REALISED: { label: 'Realised', variant: 'success' },
  RETURNED: { label: 'Returned / Bounced', variant: 'danger' },
  MEMBER_RETURNED: { label: 'Returned to Member', variant: 'danger' },
};

const PAYMENT_TYPE_LABELS: Record<string, string> = {
  GPAY: 'GPay',
  NEFT: 'NEFT',
  RTGS: 'RTGS',
  CHEQUE: 'Cheque',
  UPI: 'UPI',
  'E-NACH': 'E-NACH',
};

const bankRegisterConfig = defineListPage<BankInstrument>({
  variant: 'data-table',
  resource: 'bank-instruments',
  searchable: true,
  columns: [
    { id: 'transactionDate', header: 'Date', accessor: 'transactionDate', sortable: true },
    { id: 'paymentType', header: 'Type', accessor: 'paymentType' },
    { id: 'instrumentNumber', header: 'Instrument', accessor: 'instrumentNumber' },
    { id: 'bankName', header: 'Bank / Ref', accessor: 'bankName' },
    { id: 'totalAmountPaise', header: 'Amount', accessor: 'totalAmountPaise', numeric: true },
    { id: 'status', header: 'Status', accessor: 'status' },
  ],
  defaultPageSize: 20,
});

const fetchInstruments: PaginatedQueryFn<BankInstrument> = async (params) => {
  const qs = new URLSearchParams();
  if (params.page) qs.append('page', String(params.page));
  if (params.limit) qs.append('limit', String(params.limit));
  if (params.search) qs.append('instrumentNumber', params.search);
  if (params.status) qs.append('status', String(params.status));
  if (params.paymentType) qs.append('paymentType', String(params.paymentType));

  const res = await fetcher.get<{ data: BankInstrument[]; meta: { total: number; page: number; limit: number } }>(
    `/staff/v1/collections/bank-instruments?${qs.toString()}`
  );
  return {
    data: res.data ?? [],
    meta: res.meta ?? { total: 0, page: 1, limit: 20 },
  };
};

function ChequeRegisterPage() {
  usePageTitle('Cheque Register');
  const navigate = useNavigate();
  const search = useSearch({ strict: false });
  const { toast } = useToast();

  const [statusFilter, setStatusFilter] = React.useState('');
  const [paymentTypeFilter, setPaymentTypeFilter] = React.useState('');

  const urlAdapter = React.useMemo(
    () => createUrlAdapter(search, navigate as never),
    [search, navigate],
  );

  const fetchInstrumentsWithFilters = React.useCallback(
    async (params: Record<string, string | undefined>) => {
      const mergedParams = {
        ...params,
        ...(statusFilter ? { status: statusFilter } : {}),
        ...(paymentTypeFilter ? { paymentType: paymentTypeFilter } : {}),
      };
      return fetchInstruments(mergedParams);
    },
    [statusFilter, paymentTypeFilter],
  );

  const list = useListPage(bankRegisterConfig, fetchInstrumentsWithFilters, urlAdapter);

  // Separate non-paginated query to calculate stats across all entries
  const { data: statsResponse } = useBankInstruments({});
  const allInstruments = statsResponse?.data ?? [];

  const updateStatusMutation = useUpdateBankInstrumentStatus();

  const handleStatusUpdate = (instrument: BankInstrument, newStatus: string) => {
    const payload: { status: string; depositDate?: string; collectionDate?: string; returnDate?: string; memberReturnDate?: string } = { status: newStatus };
    if (newStatus === 'DEPOSITED') payload.depositDate = new Date().toISOString().split('T')[0];
    if (newStatus === 'REALISED') payload.collectionDate = new Date().toISOString().split('T')[0];
    if (newStatus === 'RETURNED') payload.returnDate = new Date().toISOString().split('T')[0];
    if (newStatus === 'MEMBER_RETURNED') payload.memberReturnDate = new Date().toISOString().split('T')[0];

    updateStatusMutation.mutate(
      { id: instrument.id, ...payload },
      {
        onSuccess: () => {
          toast({
            type: 'success',
            title: 'Status Updated',
            description: `${PAYMENT_TYPE_LABELS[instrument.paymentType] || instrument.paymentType} ${instrument.instrumentNumber || ''} marked as ${STATUS_CONFIG[newStatus]?.label || newStatus}.`,
          });
          void list.refetch();
        },
        onError: (err) => {
          toast({
            type: 'error',
            title: 'Failed to Update',
            description: (err as { message?: string }).message || 'Could not update instrument status.',
          });
        },
      },
    );
  };

  const configuredBankRegisterConfig = React.useMemo(
    () => ({
      ...bankRegisterConfig,
      rowActions: [
        {
          label: 'Deposit Instrument',
          icon: <Clock className="w-4 h-4" />,
          hidden: (row: BankInstrument) => row.status !== 'RECEIVED',
          onClick: (row: BankInstrument) => handleStatusUpdate(row, 'DEPOSITED'),
        },
        {
          label: 'Realise Instrument',
          icon: <CheckCircle className="w-4 h-4" />,
          hidden: (row: BankInstrument) => row.status !== 'DEPOSITED',
          onClick: (row: BankInstrument) => handleStatusUpdate(row, 'REALISED'),
        },
        {
          label: 'Bounce / Return',
          icon: <RotateCcw className="w-4 h-4" />,
          variant: 'destructive' as const,
          confirmTitle: 'Bounce Instrument',
          confirmDescription: 'Are you sure you want to mark this instrument as bounced/returned?',
          confirmLabel: 'Confirm Bounce',
          hidden: (row: BankInstrument) => row.status !== 'RECEIVED' && row.status !== 'DEPOSITED',
          onClick: (row: BankInstrument) => handleStatusUpdate(row, 'RETURNED'),
        },
        {
          label: 'Return to Member',
          icon: <RotateCcw className="w-4 h-4" />,
          hidden: (row: BankInstrument) => row.status !== 'RETURNED',
          onClick: (row: BankInstrument) => handleStatusUpdate(row, 'MEMBER_RETURNED'),
        },
      ],
    }),
    [handleStatusUpdate],
  );

  const totalReceivedPaise = allInstruments
    .filter((i) => i.status === 'RECEIVED')
    .reduce((sum, i) => sum + i.totalAmountPaise, 0);
  const totalDepositedPaise = allInstruments
    .filter((i) => i.status === 'DEPOSITED')
    .reduce((sum, i) => sum + i.totalAmountPaise, 0);
  const totalRealisedPaise = allInstruments
    .filter((i) => i.status === 'REALISED')
    .reduce((sum, i) => sum + i.totalAmountPaise, 0);
  const totalReturnedPaise = allInstruments
    .filter((i) => i.status === 'RETURNED')
    .reduce((sum, i) => sum + i.totalAmountPaise, 0);

  return (
    <div className="flex flex-col w-full h-full" style={{ viewTransitionName: 'page-body' }}>
      <PageHeader
        title="Cheque Register"
        subtitle="Record and manage all incoming cheques and bank payments (Cheques, UPI, NEFT, RTGS)."
        action={
          <Button variant="primary" onClick={() => navigate({ to: '/finance/cheque-register/new' })}>
            Record Cheque / Payment
          </Button>
        }
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-surface-1 border border-border rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs text-text-muted font-semibold uppercase tracking-wider">Received</span>
              <p className="text-lg font-bold font-mono text-info-text mt-1 tnum"><Money paise={totalReceivedPaise} /></p>
            </div>
            <div className="p-2.5 bg-info-500/10 text-info-text rounded-lg"><Clock className="w-5 h-5" /></div>
          </div>
        </div>
        <div className="bg-surface-1 border border-border rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs text-text-muted font-semibold uppercase tracking-wider">Deposited</span>
              <p className="text-lg font-bold font-mono text-warning-text mt-1 tnum"><Money paise={totalDepositedPaise} /></p>
            </div>
            <div className="p-2.5 bg-warning-500/10 text-warning-text rounded-lg"><Banknote className="w-5 h-5" /></div>
          </div>
        </div>
        <div className="bg-surface-1 border border-border rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs text-text-muted font-semibold uppercase tracking-wider">Realised</span>
              <p className="text-lg font-bold font-mono text-success-text mt-1 tnum"><Money paise={totalRealisedPaise} /></p>
            </div>
            <div className="p-2.5 bg-success-500/10 text-success-text rounded-lg"><CheckCircle className="w-5 h-5" /></div>
          </div>
        </div>
        <div className="bg-surface-1 border border-border rounded-xl p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div>
              <span className="text-xs text-text-muted font-semibold uppercase tracking-wider">Returned / Bounced</span>
              <p className="text-lg font-bold font-mono text-danger-text mt-1 tnum"><Money paise={totalReturnedPaise} /></p>
            </div>
            <div className="p-2.5 bg-danger-500/10 text-danger-text rounded-lg"><RotateCcw className="w-5 h-5" /></div>
          </div>
        </div>
      </div>

      <div className="flex-1 w-full min-h-0">
        <DataTable
          config={configuredBankRegisterConfig}
          list={list}
          toolbarActions={
            <>
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  list.refetch();
                }}
                className="h-9 px-3 bg-surface-1 border border-border rounded-[6px] text-sm text-text-primary focus:outline-none focus:border-accent-500 font-semibold"
              >
                <option value="">All Statuses</option>
                <option value="RECEIVED">Received</option>
                <option value="DEPOSITED">Deposited</option>
                <option value="REALISED">Realised</option>
                <option value="RETURNED">Returned / Bounced</option>
                <option value="MEMBER_RETURNED">Returned to Member</option>
              </select>
              <select
                value={paymentTypeFilter}
                onChange={(e) => {
                  setPaymentTypeFilter(e.target.value);
                  list.refetch();
                }}
                className="h-9 px-3 bg-surface-1 border border-border rounded-[6px] text-sm text-text-primary focus:outline-none focus:border-accent-500 font-semibold"
              >
                <option value="">All Types</option>
                <option value="CHEQUE">Cheque</option>
                <option value="UPI">UPI</option>
                <option value="GPAY">GPay</option>
                <option value="NEFT">NEFT</option>
                <option value="RTGS">RTGS</option>
                <option value="E-NACH">E-NACH</option>
              </select>
              {(list.search || statusFilter || paymentTypeFilter) && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-text-muted hover:text-text-primary transition-colors"
                  onClick={() => {
                    list.setSearch('');
                    setStatusFilter('');
                    setPaymentTypeFilter('');
                  }}
                >
                  Reset
                </Button>
              )}
            </>
          }
          customRenderers={{
            transactionDate: (row) => (
              <span className="font-mono text-xs whitespace-nowrap tnum">
                {new Date(row.transactionDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
              </span>
            ),
            paymentType: (row) => (
              <span className="font-semibold text-xs">{PAYMENT_TYPE_LABELS[row.paymentType] || row.paymentType}</span>
            ),
            instrumentNumber: (row) => (
              <span className="font-mono text-xs font-semibold">{row.instrumentNumber || '-'}</span>
            ),
            bankName: (row) => (
              <div className="flex flex-col">
                <span className="text-xs font-semibold">{row.bankName || '-'}</span>
                {row.bankAccountNumber && (
                  <span className="font-mono text-xs text-text-muted mt-0.5">A/C {row.bankAccountNumber}</span>
                )}
              </div>
            ),
            totalAmountPaise: (row) => (
              <span className="font-semibold font-mono tnum text-right block">
                <Money paise={row.totalAmountPaise} />
              </span>
            ),
            status: (row) => {
              const statusCfg = STATUS_CONFIG[row.status] ?? { label: row.status, variant: 'info' as const };
              return <StatusBadge status={statusCfg.variant} label={statusCfg.label} />;
            },
          }}
        />
      </div>
    </div>
  );
}
